
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import FileUpload from "@/components/FileUpload";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { AvatarUpload } from "@/components/AvatarUpload";
import { AuthForm } from "@/components/auth/AuthForm";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { TransfersList } from "@/components/transfers/TransfersList";
import { useIsMobile } from "@/hooks/use-mobile";

interface Profile {
  id: string;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
}

const Index = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [transfers, setTransfers] = useState<any[]>([]);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      console.log("Session loaded:", session); // Agregado para debug
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      console.log("Auth state changed:", session); // Agregado para debug
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      console.log("User exists, fetching data..."); // Agregado para debug
      fetchTransfers();
      fetchProfile();
    } else {
      console.log("No user, clearing data..."); // Agregado para debug
      setTransfers([]);
      setProfile(null);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      console.log("Fetching profile..."); // Agregado para debug
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      if (!data) {
        console.log("No profile found, creating new one..."); // Agregado para debug
        try {
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert([{ id: user.id }])
            .select()
            .single();

          if (createError) {
            if (createError.code === '23505') {
              const { data: existingProfile, error: fetchError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

              if (fetchError) {
                console.error('Error fetching existing profile:', fetchError);
                return;
              }

              setProfile(existingProfile);
              return;
            }

            console.error('Error creating profile:', createError);
            return;
          }

          setProfile(newProfile);
        } catch (error) {
          console.error('Error in profile creation:', error);
        }
      } else {
        console.log("Profile found:", data); // Agregado para debug
        setProfile(data);
      }
    } catch (error) {
      console.error('Error in profile management:', error);
    }
  };

  const fetchTransfers = async () => {
    const { data, error } = await supabase
      .from('transfers')
      .select(`
        *,
        shared_links (
          id,
          access_count
        )
      `)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load your transfers",
        variant: "destructive",
      });
    } else {
      setTransfers(data || []);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
  };

  const getDownloadUrl = async (filePath: string) => {
    const { data } = await supabase.storage
      .from('transfers')
      .createSignedUrl(filePath, 60 * 60); // 1 hour expiry

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const copyToClipboard = async (transferId: string, filePath: string) => {
    try {
      console.log("Creando shared link para transferId:", transferId);
      
      // Verificamos si ya existe un shared_link para esta transferencia
      const { data: existingLinks, error: checkError } = await supabase
        .from('shared_links')
        .select('id')
        .eq('transfer_id', transferId)
        .maybeSingle();
      
      if (checkError) {
        console.error("Error al verificar links existentes:", checkError);
      }
      
      let linkId;
      
      if (existingLinks && existingLinks.id) {
        // Usamos el link existente
        linkId = existingLinks.id;
        console.log("Usando link existente:", linkId);
      } else {
        // Creamos un nuevo shared_link
        const { data: newLink, error: createError } = await supabase
          .from('shared_links')
          .insert({
            transfer_id: transferId,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 días
          })
          .select('id')
          .single();

        if (createError) {
          console.error("Error al crear shared_link:", createError);
          throw new Error("No se pudo generar el enlace");
        }
        
        linkId = newLink.id;
        console.log("Nuevo link creado:", linkId);
      }

      const downloadUrl = `${window.location.origin}/download?id=${linkId}`;
      await navigator.clipboard.writeText(downloadUrl);
      
      toast({
        title: "¡Enlace copiado!",
        description: "El enlace de descarga ha sido copiado al portapapeles",
      });
      
      // Refrescamos la lista de transferencias para mostrar el contador actualizado
      fetchTransfers();
      
    } catch (error: any) {
      console.error("Error al copiar enlace:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo copiar el enlace",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (transferId: string) => {
    try {
      const { error } = await supabase.rpc('mark_transfer_deleted', {
        transfer_id: transferId
      });

      if (error) throw error;

      setTransfers(transfers.filter(transfer => transfer.id !== transferId));

      toast({
        title: "Archivo eliminado",
        description: "El archivo ha sido eliminado correctamente",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el archivo",
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <div className="w-full">
        {/* Header with Auth Menu */}
        <div className="relative w-full px-4 sm:px-8 py-4 flex justify-between items-center">
          {/* Logo */}
          <img 
            src="https://tv.whatstudio.es/wp-content/uploads/2025/02/logo_white.svg" 
            alt="Logo Corporativo" 
            className={`${isMobile ? 'h-[4rem]' : 'h-[7rem]'} w-auto transition-all duration-300`}
          />

          {/* Auth Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className="bg-white/90 hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
              >
                {user ? (
                  <>
                    <AvatarUpload 
                      userId={user.id}
                      currentAvatarUrl={profile?.avatar_url}
                      onAvatarUpdate={(url) => setProfile(prev => prev ? {...prev, avatar_url: url} : null)}
                      size="sm"
                      showUploadButton={false}
                    />
                    <span className="hidden sm:inline">
                      {profile?.first_name || user.email?.split('@')[0]}
                    </span>
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:w-[600px] md:w-[800px] lg:w-[800px] bg-[#0A0D14] p-0">
              <div className="h-full flex flex-col">
                <SheetHeader className="p-6 sm:p-8 border-b border-[#2E2F33]">
                  <SheetTitle className="text-xl sm:text-2xl font-bold text-white">
                    {user ? "Mi Cuenta" : "Bienvenido"}
                  </SheetTitle>
                </SheetHeader>

                {user ? (
                  <Tabs defaultValue="account" className="flex-1">
                    <TabsList className="px-6 sm:px-8 grid w-full grid-cols-2 bg-[#1A1B1E] border-b border-[#2E2F33]">
                      <TabsTrigger 
                        value="account" 
                        className="text-white/60 data-[state=active]:bg-[#2E2F33] data-[state=active]:text-white"
                      >
                        Tu Cuenta
                      </TabsTrigger>
                      <TabsTrigger 
                        value="transfers" 
                        className="text-white/60 data-[state=active]:bg-[#2E2F33] data-[state=active]:text-white"
                      >
                        Tus Transferencias
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="account" className="flex-1 p-6 sm:p-8 space-y-4 overflow-auto">
                      <ProfileForm 
                        userId={user.id}
                        profile={profile}
                        onProfileUpdate={setProfile}
                        email={user.email || ""}
                      />
                      <Button 
                        onClick={handleLogout} 
                        className="w-full bg-[#00F5D0] hover:bg-[#00F5D0]/90 text-black font-medium h-11"
                      >
                        Cerrar Sesión
                      </Button>
                    </TabsContent>

                    <TabsContent value="transfers" className="flex-1 p-6 sm:p-8 overflow-auto">
                      <TransfersList 
                        transfers={transfers}
                        onCopy={copyToClipboard}
                        onDownload={getDownloadUrl}
                        onDelete={handleDelete}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="flex-1">
                    <AuthForm />
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Content Container */}
        <div className="relative h-[calc(100vh-9rem)] flex items-center justify-center sm:justify-start px-4 sm:px-0">
          {/* Left Side Container */}
          <div className="w-full max-w-[24rem] sm:ml-24">
            {/* Upload Components */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 sm:p-6 shadow-lg border border-white/10">
              <div className="flex flex-col items-center">
                <FileUpload />
              </div>
            </div>
          </div>
        </div>

        {/* Powered by text */}
        <div className="absolute bottom-4 right-4 sm:right-8 text-white/80 text-xs sm:text-sm">
          Powered by <a 
            href="http://whatstudio.co.uk/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="font-bold hover:text-white transition-colors"
          >
            WHATS Studio
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default Index;
